import { useParams, useNavigate } from 'react-router-dom';
import { LessonContent } from '@/components/lesson/LessonContent';
import { InteractiveWorkspace } from '@/components/lesson/InteractiveWorkspace';
import { getLessonById, getNextLesson, course, preloadNextModule } from '@/data/index';
import { useLessonStore } from '@/store/lessonStore';
import { useGitStore } from '@/store/gitStore';
import { useEffect, useState } from 'react';

/**
 * Страница урока с теорией и интерактивным блоком
 */
export default function LessonPage() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const { startLesson, completeStep } = useLessonStore();
  const { repo } = useGitStore();
  const [isCompleted, setIsCompleted] = useState(false);

  // Получаем данные урока
  const lesson = getLessonById(lessonId ?? '1-1');

  // Получаем данные модуля
  const module = course.modules.find((m) => m.id === lesson?.moduleId);

  // Инициализация урока
  useEffect(() => {
    if (lessonId) {
      startLesson(lessonId);
      // Сбрасываем состояние завершения при смене урока
      setIsCompleted(false);

      // Предзагружаем следующий модуль для оптимизации
      const currentModuleId = getLessonById(lessonId)?.moduleId;
      if (currentModuleId) {
        preloadNextModule(currentModuleId);
      }
    }
  }, [lessonId, startLesson]);

  // Обработка завершения шага
  const handleStepComplete = (stepId: number) => {
    if (lessonId) {
      completeStep(lessonId, stepId);
    }
  };

  // Обработка завершения урока
  const handleLessonComplete = () => {
    setIsCompleted(true);

    // Сохраняем состояние Git для этого урока
    const gitState = JSON.stringify(repo);
    if (lessonId) {
      useLessonStore.getState().saveGitState(lessonId, gitState);
    }
  };

  // Переход к следующему уроку
  const goToNextLesson = () => {
    const next = getNextLesson(lessonId ?? '1-1');
    if (next) {
      navigate(`/lesson/${next.id}`);
    } else {
      // Все уроки пройдены
      navigate('/');
    }
  };

  // Если урок не найден
  if (!lesson || !module) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-200">Lesson not found</h2>
          <p className="mt-2 text-slate-400">The requested lesson could not be found.</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 rounded-lg bg-git-orange px-4 py-2 text-white hover:bg-git-orange/90"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col lg:flex-row">
      {/* Теоретическая часть */}
      <div className="flex-1 overflow-y-auto border-r border-slate-700">
        <LessonContent
          title={lesson.title}
          module={module.title}
          theory={lesson.theory}
          keyInsight={lesson.keyInsight ?? undefined}
          estimatedTime={lesson.estimatedTime}
        />
      </div>

      {/* Интерактивный блок */}
      <div className="flex-1 lg:max-w-[600px] min-h-[400px]">
        {isCompleted ? (
          <div className="flex h-full flex-col items-center justify-center p-8 text-center">
            <div className="mb-4 text-5xl">🎉</div>
            <h2 className="text-xl font-semibold text-slate-200">Lesson Complete!</h2>
            <p className="mt-2 text-slate-400">You've successfully completed "{lesson.title}".</p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setIsCompleted(false)}
                className="rounded-lg border border-slate-600 px-4 py-2 text-slate-300 hover:bg-slate-800"
              >
                Practice Again
              </button>
              <button
                onClick={goToNextLesson}
                className="rounded-lg bg-git-orange px-4 py-2 text-white hover:bg-git-orange/90"
              >
                Next Lesson
              </button>
            </div>
          </div>
        ) : (
          <InteractiveWorkspace
            steps={lesson.steps}
            lessonId={lesson.id}
            onStepComplete={handleStepComplete}
            onLessonComplete={handleLessonComplete}
          />
        )}
      </div>
    </div>
  );
}
